import ActionableMessageCardStrings from '../store/schema/ActionableMessageCardStrings';
import {
    IValidationError,
    ValidationResults,
    ValidationError,
    Input,
    getBoolValue,
    getStringValue,
    setProperty,
} from 'adaptivecards';
import { NumberTextField } from './NumberTextField';

import { NumberValidation, validateNumber } from '../utils/validateNumber';
import * as React from 'react';
import * as ReactDOM from 'react-dom';
import styles from '../styles/cardStyles.scss';

export default class CustomNumberInput extends Input {
    private numberTextFieldElement: HTMLDivElement;
    private currentValue: string;
    private _min: string;
    private _max: string;

    public static strings: ActionableMessageCardStrings;

    public placeholder: string;
    public isRequired: boolean = false;

    private getNumberString = (num: number): string => {
        return typeof num === 'number' ? num.toString() : num;
    };

    private onTextFieldValueChanged = (value: string) => {
        this.currentValue = value;
    };

    protected internalRender(): HTMLElement {
        this.numberTextFieldElement = document.createElement('div');
        this.numberTextFieldElement.className = styles.inputElementWrapper;
        const placeholderValue =
            this.placeholder || CustomNumberInput.strings.placeholder.numberInput;
        ReactDOM.render(
            <React.StrictMode>
                <NumberTextField
                    placeholder={placeholderValue}
                    onValueChanged={this.onTextFieldValueChanged}
                    min={this.min}
                    max={this.max}
                    required={this.isRequired}
                    value={this.defaultValue}
                    strings={CustomNumberInput.strings}
                />
            </React.StrictMode>,
            this.numberTextFieldElement
        );
        return this.numberTextFieldElement;
    }

    public toJSON(): any {
        let result = super.toJSON();
        setProperty(result, 'min', this.min);
        setProperty(result, 'max', this.max);
        setProperty(result, 'placeholder', this.placeholder);
        setProperty(result, 'isRequired', this.isRequired, false);
        return result;
    }

    public parse(json: any, errors?: Array<IValidationError>) {
        super.parse(json, errors);
        this.min = this.getNumberString(json['min']);
        this.max = this.getNumberString(json['max']);
        this.placeholder = getStringValue(json['placeholder']);
        this.isRequired = getBoolValue(json['isRequired'], this.isRequired);
    }

    public remove(): boolean {
        if (this.numberTextFieldElement) {
            ReactDOM.unmountComponentAtNode(this.numberTextFieldElement);
            this.numberTextFieldElement = null;
        }

        return super.remove();
    }

    public internalValidateProperties(context: ValidationResults) {
        if (this.isRequired && !this.currentValue) {
            context.addFailure(this, {
                error: ValidationError.InvalidPropertyValue,
                message: CustomNumberInput.strings.warning.missingRequiredInput,
            });
        } else if (validateNumber(this.value, this.min, this.max) !== NumberValidation.Ok) {
            context.addFailure(this, {
                error: ValidationError.InvalidPropertyValue,
                message: CustomNumberInput.strings.warning.invalidInput,
            });
        }
    }

    public getJsonTypeName(): string {
        return 'Input.Number';
    }

    get min(): string {
        return this._min;
    }

    set min(value: string) {
        this._min = this.parseInputValue(value);
    }

    get max(): string {
        return this._max;
    }

    set max(value: string) {
        this._max = this.parseInputValue(value);
    }

    get value(): string {
        return this.currentValue;
    }
}
